import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck,
  AlertTriangle,
  Database,
  Trash2,
  RefreshCw,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileWarning,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/config';

const DataAudit = () => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState({});
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const token = localStorage.getItem('hw_token');

  const runAudit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/data-audit`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAuditData(data);
        toast.success('Audit completato');
      } else {
        toast.error('Errore nell\'audit');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const resolveDuplicate = async (keepId, removeIds, groupIdx) => {
    setResolving(prev => ({ ...prev, [groupIdx]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/data-audit/resolve-duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ keep_id: keepId, remove_ids: removeIds })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setAuditData(prev => ({
          ...prev,
          duplicates: prev.duplicates.filter((_, i) => i !== groupIdx),
          duplicate_count: prev.duplicate_count - 1
        }));
      } else {
        toast.error(data.detail || 'Errore nella risoluzione');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setResolving(prev => ({ ...prev, [groupIdx]: false }));
    }
  };

  const cleanupOrphans = async () => {
    setCleaningOrphans(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/data-audit/cleanup-orphans`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message);
        setAuditData(prev => ({ ...prev, orphan_tokens: 0, orphan_documents: 0 }));
      } else {
        toast.error(data.detail || 'Errore nella pulizia');
      }
    } catch (e) {
      toast.error('Errore di connessione');
    } finally {
      setCleaningOrphans(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-emerald-50 border-emerald-200';
    if (score >= 70) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-8 w-8 text-slate-700" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="audit-page-title">Audit Dati</h1>
              <p className="text-sm text-slate-500">Verifica integrita' e pulizia del database</p>
            </div>
          </div>
          <Button
            onClick={runAudit}
            disabled={loading}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="run-audit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {loading ? 'Analisi in corso...' : 'Avvia Audit'}
          </Button>
        </div>

        {!auditData && !loading && (
          <Card className="p-12 text-center border-dashed border-2 border-slate-200" data-testid="audit-empty-state">
            <Database className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 text-lg mb-2">Nessun audit eseguito</p>
            <p className="text-slate-400 text-sm">Clicca "Avvia Audit" per analizzare l'integrita' dei dati</p>
          </Card>
        )}

        {auditData && (
          <div className="space-y-6">
            {/* Health Score + Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className={`p-5 border ${getScoreBg(auditData.health_score)}`} data-testid="health-score-card">
                <div className="flex items-center gap-3">
                  <Activity className={`h-8 w-8 ${getScoreColor(auditData.health_score)}`} />
                  <div>
                    <p className={`text-3xl font-bold ${getScoreColor(auditData.health_score)}`}>
                      {auditData.health_score}%
                    </p>
                    <p className="text-xs text-slate-500">Punteggio Salute</p>
                  </div>
                </div>
              </Card>
              <Card className="p-5" data-testid="total-bookings-card">
                <p className="text-3xl font-bold text-slate-700">{auditData.bookings?.total?.toLocaleString('it-IT')}</p>
                <p className="text-xs text-slate-500">Prenotazioni Totali</p>
              </Card>
              <Card className="p-5" data-testid="duplicate-count-card">
                <p className={`text-3xl font-bold ${auditData.duplicate_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {auditData.duplicate_count}
                </p>
                <p className="text-xs text-slate-500">Gruppi Duplicati</p>
              </Card>
              <Card className="p-5" data-testid="orphan-count-card">
                <p className={`text-3xl font-bold ${(auditData.orphan_tokens + auditData.orphan_documents) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {auditData.orphan_tokens + auditData.orphan_documents}
                </p>
                <p className="text-xs text-slate-500">Record Orfani</p>
              </Card>
            </div>

            {/* Orphans Section */}
            {(auditData.orphan_tokens > 0 || auditData.orphan_documents > 0) && (
              <Card className="p-5 border-amber-200 bg-amber-50" data-testid="orphans-section">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileWarning className="h-6 w-6 text-amber-600" />
                    <div>
                      <h3 className="font-semibold text-slate-800">Record Orfani</h3>
                      <p className="text-sm text-slate-500">
                        {auditData.orphan_tokens} token ospiti + {auditData.orphan_documents} documenti senza prenotazione associata
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={cleanupOrphans}
                    disabled={cleaningOrphans}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="cleanup-orphans-btn"
                  >
                    {cleaningOrphans ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Pulisci Orfani
                  </Button>
                </div>
              </Card>
            )}

            {/* All Clear */}
            {auditData.duplicate_count === 0 && auditData.orphan_tokens === 0 && auditData.orphan_documents === 0 && (
              <Card className="p-6 bg-emerald-50 border-emerald-200 text-center" data-testid="all-clear-card">
                <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                <p className="font-semibold text-emerald-700">Database pulito</p>
                <p className="text-sm text-emerald-600">Nessun duplicato o record orfano trovato</p>
              </Card>
            )}

            {/* Duplicates List */}
            {auditData.duplicates?.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Prenotazioni Duplicate ({auditData.duplicates.length})
                </h2>
                <div className="space-y-3">
                  {auditData.duplicates.map((group, idx) => (
                    <Card
                      key={idx}
                      className={`border ${group.has_questura ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                      data-testid={`duplicate-group-${idx}`}
                    >
                      <div
                        className="p-4 cursor-pointer flex items-center justify-between"
                        onClick={() => setExpandedGroup(expandedGroup === idx ? null : idx)}
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="font-medium text-slate-800">{group.guest_name}</span>
                            <span className="text-sm text-slate-400 ml-3">{group.property_name}</span>
                          </div>
                          <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">
                            Check-in: {group.check_in}
                          </span>
                          <span className="text-xs bg-amber-100 px-2 py-1 rounded text-amber-700">
                            {group.count} record
                          </span>
                          {group.has_questura && (
                            <span className="text-xs bg-red-500 text-white px-2 py-1 rounded">QUESTURA</span>
                          )}
                        </div>
                        {expandedGroup === idx ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </div>

                      {expandedGroup === idx && (
                        <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                          <div className="space-y-2">
                            {group.records.map((rec, recIdx) => {
                              const isSync = rec.sync_source === 'hw_api' || rec.source === 'hw_sync' || rec.hw_updated_at;
                              return (
                              <div
                                key={rec.id}
                                className={`flex items-center justify-between p-3 rounded-lg border ${isSync ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-100'}`}
                                data-testid={`record-${rec.id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isSync ? 'bg-blue-200 text-blue-800' : 'bg-orange-200 text-orange-800'}`}>
                                      {isSync ? 'SYNC HW' : 'MANUALE'}
                                    </span>
                                    <span className="text-sm font-medium text-slate-700">{rec.booking_code}</span>
                                    {rec.cancelled && (
                                      <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Cancellata</span>
                                    )}
                                    {rec.questura_sent && (
                                      <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Questura</span>
                                    )}
                                    {rec.status && (
                                      <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{rec.status}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                    <span>Check-out: {rec.check_out}</span>
                                    {rec.guest_total > 0 && <span className="font-medium text-slate-700">Ospite: €{Number(rec.guest_total).toFixed(2)}</span>}
                                    {rec.owner_amount > 0 && <span>Proprietario: €{Number(rec.owner_amount).toFixed(2)}</span>}
                                    {rec.tourist_tax > 0 && <span>Tassa: €{Number(rec.tourist_tax).toFixed(2)}</span>}
                                    {rec.created_at && <span>Creato: {new Date(rec.created_at).toLocaleDateString('it-IT')}</span>}
                                  </div>
                                  <code className="text-[10px] text-slate-400 mt-0.5 block">{rec.id}</code>
                                </div>
                                <div className="flex gap-2 ml-3">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    disabled={resolving[idx]}
                                    onClick={() => {
                                      const removeIds = group.records.filter(r => r.id !== rec.id).map(r => r.id);
                                      resolveDuplicate(rec.id, removeIds, idx);
                                    }}
                                    data-testid={`keep-btn-${rec.id}`}
                                  >
                                    {resolving[idx] ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                                    Mantieni
                                  </Button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                          {group.has_questura && (
                            <p className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">
                              Attenzione: questo gruppo contiene record con flag Questura. Verifica manualmente prima di eliminare.
                            </p>
                          )}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Audit timestamp */}
            <p className="text-xs text-slate-400 text-right" data-testid="audit-timestamp">
              Ultima analisi: {new Date(auditData.run_at).toLocaleString('it-IT')}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DataAudit;
